/**
 * Image registry.
 * Authentic NCPL factory photographs keep factory-* filenames.
 * The sewing floor is shown on the company sections. Cutting and finishing stay held.
 * Series A = product/construction stills. Series B = operations.
 * Kids stills live under assets/originals/kids/.
 * Men’s lookbook stills live under assets/originals/mens/.
 * Generated images must not be captioned as NCPL’s factory.
 */

const FACTORY_WIDTHS = [800, 1200, 1600];
const PRODUCT_WIDTHS = [800, 819];
const KIDS_WIDTHS = [800, 1122];
const DETAIL_WIDTHS = [800, 1024];
const OP_WIDTHS = [800, 1024];
const CHECKUP_WIDTHS = [800, 1200, 1600];
const HERO_WIDTHS = [800, 1200, 1600, 1672];
const PORTRAIT_WIDTHS = [480, 820];

function makeImage({ id, alt, prefix, width, height, objectPosition, widths, fallbackWidth }) {
  const resolvedFallback = fallbackWidth || widths[widths.length - 1];
  return {
    id,
    alt,
    width,
    height,
    objectPosition: objectPosition || 'center',
    sources: {
      webp: widths.map((w) => ({ src: `/images/${prefix}-${w}.webp`, width: w })),
      jpg: widths.map((w) => ({ src: `/images/${prefix}-${w}.jpg`, width: w })),
    },
    fallback: `/images/${prefix}-${resolvedFallback}.jpg`,
  };
}

export const images = {
  sewingFloor: makeImage({
    id: 'sewing-floor',
    alt: 'Sewing floor at Narayanee Clothing in Kolkata, with operators working at industrial sewing machines.',
    prefix: 'factory-sewing-floor',
    width: 1600,
    height: 1128,
    objectPosition: 'center 42%',
    widths: FACTORY_WIDTHS,
    fallbackWidth: 1200,
  }),
  cuttingTable: makeImage({
    id: 'cutting-table',
    alt: 'Fabric being cut on a marked lay at NCPL’s cutting section in Kolkata.',
    prefix: 'factory-cutting-table',
    width: 1600,
    height: 1128,
    objectPosition: 'center 45%',
    widths: FACTORY_WIDTHS,
    fallbackWidth: 1200,
  }),
  finishingLine: makeImage({
    id: 'finishing-line',
    alt: 'Finishing team inspecting and preparing shirts along a production table at NCPL.',
    prefix: 'factory-finishing-line',
    width: 1600,
    height: 1140,
    objectPosition: 'center 40%',
    widths: FACTORY_WIDTHS,
    fallbackWidth: 1200,
  }),
  heroShirtCollar: makeImage({
    id: 'hero-shirt-collar',
    alt: 'A white men’s formal shirt being checked at the collar, with a sewing floor out of focus behind.',
    prefix: 'hero-shirt-collar',
    width: 1672,
    height: 941,
    objectPosition: '72% 40%',
    widths: HERO_WIDTHS,
    fallbackWidth: 1200,
  }),
  productFormal: makeImage({
    id: 'product-formal-shirt',
    alt: 'A folded white men’s formal shirt with a fused collar, placket and chest pocket.',
    prefix: 'product-formal-shirt',
    width: 819,
    height: 1024,
    objectPosition: 'center 38%',
    widths: PRODUCT_WIDTHS,
  }),
  productCasual: makeImage({
    id: 'product-casual-shirt',
    alt: 'A blue check men’s casual shirt, shown folded and with the sleeve open.',
    prefix: 'product-casual-shirt',
    width: 819,
    height: 1024,
    objectPosition: 'center 42%',
    widths: PRODUCT_WIDTHS,
  }),
  productPartywear: makeImage({
    id: 'product-partywear-shirt',
    alt: 'A navy men’s occasion shirt laid on a wooden finishing table.',
    prefix: 'product-partywear-shirt',
    width: 819,
    height: 1024,
    objectPosition: 'center 40%',
    widths: PRODUCT_WIDTHS,
  }),
  kidsGinghamBreakfast: makeImage({
    id: 'kids-shirt-gingham-breakfast',
    alt: 'A kids’ short-sleeve navy gingham shirt with light-blue panels and breakfast embroidery.',
    prefix: 'kids-shirt-gingham-breakfast',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsNavyPolo: makeImage({
    id: 'kids-shirt-navy-polo',
    alt: 'A kids’ navy textured polo shirt with chest embroidery.',
    prefix: 'kids-shirt-navy-polo',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsSportsPrint: makeImage({
    id: 'kids-shirt-sports-print',
    alt: 'A kids’ navy camp-collar shirt with a white sports print.',
    prefix: 'kids-shirt-sports-print',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsPlaidPatches: makeImage({
    id: 'kids-shirt-plaid-patches',
    alt: 'A kids’ cream tartan shirt with outdoor-theme patches.',
    prefix: 'kids-shirt-plaid-patches',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsSafari: makeImage({
    id: 'kids-shirt-safari',
    alt: 'A kids’ sand short-sleeve shirt with a safari pocket and appliqué.',
    prefix: 'kids-shirt-safari',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsBuffaloHooded: makeImage({
    id: 'kids-shirt-buffalo-hooded',
    alt: 'A kids’ navy buffalo-check hooded overshirt with flap pockets.',
    prefix: 'kids-shirt-buffalo-hooded',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsFloralNavy: makeImage({
    id: 'kids-shirt-floral-navy',
    alt: 'A kids’ navy camp-collar shirt with a white hibiscus print.',
    prefix: 'kids-shirt-floral-navy',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsTieDye: makeImage({
    id: 'kids-shirt-tie-dye',
    alt: 'A kids’ camp-collar shirt in an orange and blue wash print.',
    prefix: 'kids-shirt-tie-dye',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsGreenPrint: makeImage({
    id: 'kids-shirt-green-print',
    alt: 'A kids’ short-sleeve shirt in a green and sand watercolour print.',
    prefix: 'kids-shirt-green-print',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsInkFloral: makeImage({
    id: 'kids-shirt-ink-floral',
    alt: 'A kids’ white long-sleeve shirt with a black ink floral print.',
    prefix: 'kids-shirt-ink-floral',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsKhakiTwinPocket: makeImage({
    id: 'kids-shirt-khaki-twin-pocket',
    alt: 'A kids’ sand long-sleeve shirt with twin chest pockets and black buttons.',
    prefix: 'kids-shirt-khaki-twin-pocket',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsBrownBandCollar: makeImage({
    id: 'kids-shirt-brown-band-collar',
    alt: 'A kids’ brown band-collar shirt with twin chest pockets.',
    prefix: 'kids-shirt-brown-band-collar',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsBlueCheckHooded: makeImage({
    id: 'kids-shirt-blue-check-hooded',
    alt: 'A kids’ light-blue windowpane hooded overshirt with patch pockets.',
    prefix: 'kids-shirt-blue-check-hooded',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsWhiteToggle: makeImage({
    id: 'kids-shirt-white-toggle',
    alt: 'A kids’ cream toggle jacket with embroidered patches.',
    prefix: 'kids-shirt-white-toggle',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsBlackGraphic: makeImage({
    id: 'kids-shirt-black-graphic',
    alt: 'A kids’ black long-sleeve shirt with chest graphic lettering.',
    prefix: 'kids-shirt-black-graphic',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsGreenCrew: makeImage({
    id: 'kids-shirt-green-crew',
    alt: 'A kids’ green pinstripe short-sleeve shirt with chest lettering.',
    prefix: 'kids-shirt-green-crew',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsStripeNumber: makeImage({
    id: 'kids-shirt-stripe-number',
    alt: 'A kids’ white pinstripe shirt with a large chest number.',
    prefix: 'kids-shirt-stripe-number',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsPinstripeGraphic: makeImage({
    id: 'kids-shirt-pinstripe-graphic',
    alt: 'A kids’ white pinstripe short-sleeve shirt with chest lettering.',
    prefix: 'kids-shirt-pinstripe-graphic',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsAvocado: makeImage({
    id: 'kids-shirt-avocado',
    alt: 'A kids’ sage camp-collar shirt with avocado embroidery.',
    prefix: 'kids-shirt-avocado',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsRustPatches: makeImage({
    id: 'kids-shirt-rust-patches',
    alt: 'A kids’ rust camp-collar shirt with folk-art patches.',
    prefix: 'kids-shirt-rust-patches',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  kidsRedGingham: makeImage({
    id: 'kids-shirt-red-gingham',
    alt: 'A kids’ red gingham shirt with a cream chest pocket.',
    prefix: 'kids-shirt-red-gingham',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensWhiteFormal: makeImage({
    id: 'mens-shirt-white-formal',
    alt: 'A men’s white long-sleeve formal shirt with a point collar and button cuffs.',
    prefix: 'mens-shirt-white-formal',
    width: 1122,
    height: 1402,
    objectPosition: 'center',
    widths: KIDS_WIDTHS,
  }),
  mensNavyWindowpane: makeImage({
    id: 'mens-shirt-navy-windowpane',
    alt: 'A men’s navy long-sleeve shirt in a fine blue windowpane check with a chest pocket.',
    prefix: 'mens-shirt-navy-windowpane',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensBlueWindowpaneCamp: makeImage({
    id: 'mens-shirt-blue-windowpane-camp',
    alt: 'A men’s short-sleeve blue and white windowpane shirt with a camp collar and chest pocket.',
    prefix: 'mens-shirt-blue-windowpane-camp',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensBlueTanPlaid: makeImage({
    id: 'mens-shirt-blue-tan-plaid',
    alt: 'A men’s long-sleeve blue and tan plaid shirt with a chest pocket.',
    prefix: 'mens-shirt-blue-tan-plaid',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensFlannelTwinPocket: makeImage({
    id: 'mens-shirt-flannel-twin-pocket',
    alt: 'A men’s navy, cream and rust flannel overshirt with twin buttoned chest pockets.',
    prefix: 'mens-shirt-flannel-twin-pocket',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensBurgundyPlaid: makeImage({
    id: 'mens-shirt-burgundy-plaid',
    alt: 'A men’s burgundy long-sleeve shirt in a pale windowpane plaid.',
    prefix: 'mens-shirt-burgundy-plaid',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensOliveGrid: makeImage({
    id: 'mens-shirt-olive-grid',
    alt: 'A men’s olive short-sleeve camp-collar shirt with a white grid and chest pocket.',
    prefix: 'mens-shirt-olive-grid',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensCreamTartan: makeImage({
    id: 'mens-shirt-cream-tartan',
    alt: 'A men’s cream long-sleeve shirt in a black tartan check.',
    prefix: 'mens-shirt-cream-tartan',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensYellowStripe: makeImage({
    id: 'mens-shirt-yellow-stripe',
    alt: 'A men’s long-sleeve shirt in yellow, blue and white vertical stripes.',
    prefix: 'mens-shirt-yellow-stripe',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensCollegiatePrint: makeImage({
    id: 'mens-shirt-collegiate-print',
    alt: 'A men’s white long-sleeve shirt with a multicolour collegiate print and chest pocket.',
    prefix: 'mens-shirt-collegiate-print',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensNavyCrestPlaid: makeImage({
    id: 'mens-shirt-navy-crest-plaid',
    alt: 'A men’s navy and grey plaid button-down shirt with a small chest crest.',
    prefix: 'mens-shirt-navy-crest-plaid',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensColourBlockCheck: makeImage({
    id: 'mens-shirt-colour-block-check',
    alt: 'A men’s long-sleeve shirt in an olive, grey and navy colour-block check.',
    prefix: 'mens-shirt-colour-block-check',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensTilePrint: makeImage({
    id: 'mens-shirt-tile-print',
    alt: 'A men’s short-sleeve shirt in an aqua, rust and olive tile print with a chest pocket.',
    prefix: 'mens-shirt-tile-print',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensBandanaCamp: makeImage({
    id: 'mens-shirt-bandana-camp',
    alt: 'A men’s cream short-sleeve camp-collar shirt in a black bandana print.',
    prefix: 'mens-shirt-bandana-camp',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensNavyStripe: makeImage({
    id: 'mens-shirt-navy-stripe',
    alt: 'A men’s navy short-sleeve shirt with white vertical stripes and a chest pocket.',
    prefix: 'mens-shirt-navy-stripe',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensAquaPlaid: makeImage({
    id: 'mens-shirt-aqua-plaid',
    alt: 'A men’s long-sleeve aqua, white and navy plaid shirt with a chest pocket.',
    prefix: 'mens-shirt-aqua-plaid',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensNavyCoralTwin: makeImage({
    id: 'mens-shirt-navy-coral-twin',
    alt: 'A men’s navy, cream and coral plaid overshirt with twin chest pockets.',
    prefix: 'mens-shirt-navy-coral-twin',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensBlueOmbre: makeImage({
    id: 'mens-shirt-blue-ombre',
    alt: 'A men’s long-sleeve shirt in a soft blue and white ombré check with a chest pocket.',
    prefix: 'mens-shirt-blue-ombre',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensVintagePrint: makeImage({
    id: 'mens-shirt-vintage-print',
    alt: 'A men’s stone short-sleeve camp-collar shirt with a faded vintage print.',
    prefix: 'mens-shirt-vintage-print',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  mensBlackStripe: makeImage({
    id: 'mens-shirt-black-stripe',
    alt: 'A men’s black long-sleeve shirt with wide cream vertical stripes.',
    prefix: 'mens-shirt-black-stripe',
    width: 1122,
    height: 1402,
    objectPosition: 'center 42%',
    widths: KIDS_WIDTHS,
  }),
  collarDetail: makeImage({
    id: 'detail-collar',
    alt: 'Hands checking the collar point, top button and stitching on a light blue men’s shirt.',
    prefix: 'detail-collar',
    width: 1024,
    height: 682,
    objectPosition: 'center 45%',
    widths: DETAIL_WIDTHS,
  }),
  buttonDetail: makeImage({
    id: 'detail-button',
    alt: 'Close view of buttons and buttonholes on a men’s shirt placket.',
    prefix: 'detail-button',
    width: 1024,
    height: 682,
    objectPosition: 'center 40%',
    widths: DETAIL_WIDTHS,
  }),
  fabricLays: makeImage({
    id: 'detail-fabric-lays',
    alt: 'Shirting fabrics being unrolled and inspected on a work table.',
    prefix: 'detail-fabric-lays',
    width: 1024,
    height: 682,
    objectPosition: 'center 48%',
    widths: DETAIL_WIDTHS,
  }),
  opFabricInspection: makeImage({
    id: 'op-fabric-inspection',
    alt: 'An operator inspecting pale blue shirting fabric on a backlit inspection machine.',
    prefix: 'op-fabric-inspection',
    width: 1024,
    height: 768,
    objectPosition: 'center 42%',
    widths: OP_WIDTHS,
  }),
  opCadMarker: makeImage({
    id: 'op-cad-marker',
    alt: 'A pattern-maker planning a men’s shirt marker on a CAD screen, with paper patterns on the desk.',
    prefix: 'op-cad-marker',
    width: 1024,
    height: 768,
    objectPosition: 'center 40%',
    widths: OP_WIDTHS,
  }),
  opAutomatedCutting: makeImage({
    id: 'op-automated-cutting',
    alt: 'An automated cutter following a nested shirt marker across a multi-ply fabric lay.',
    prefix: 'op-automated-cutting',
    width: 1024,
    height: 576,
    objectPosition: 'center 48%',
    widths: OP_WIDTHS,
  }),
  opCuttingTable: makeImage({
    id: 'op-cutting-table',
    alt: 'A cutter working a marked fabric lay with a straight knife on a long cutting table.',
    prefix: 'op-cutting-table',
    width: 1024,
    height: 768,
    objectPosition: 'center 45%',
    widths: OP_WIDTHS,
  }),
  opSewingFloor: makeImage({
    id: 'op-sewing-floor',
    alt: 'Operators sewing men’s shirts along production lines of industrial lockstitch machines.',
    prefix: 'op-sewing-floor',
    width: 819,
    height: 1023,
    objectPosition: 'center 48%',
    widths: PRODUCT_WIDTHS,
  }),
  opSewingClose: makeImage({
    id: 'op-sewing-close',
    alt: 'Close view of an industrial sewing machine stitching a men’s shirt seam.',
    prefix: 'op-sewing-close',
    width: 1024,
    height: 768,
    objectPosition: 'center 45%',
    widths: OP_WIDTHS,
  }),
  opCollarCuff: makeImage({
    id: 'op-collar-cuff',
    alt: 'Collar pieces being finished on a dedicated machine, with cut collars stacked beside the shirts.',
    prefix: 'op-collar-cuff',
    width: 1024,
    height: 768,
    objectPosition: 'center 42%',
    widths: OP_WIDTHS,
  }),
  opFusing: makeImage({
    id: 'op-fusing',
    alt: 'Collar and cuff panels being fed through a fusing press to bond interlining.',
    prefix: 'op-fusing',
    width: 1024,
    height: 768,
    objectPosition: 'center 45%',
    widths: OP_WIDTHS,
  }),
  opFinishingIron: makeImage({
    id: 'op-finishing-iron',
    alt: 'A presser steam-ironing a white men’s formal shirt in a finishing hall.',
    prefix: 'op-finishing-iron',
    width: 1024,
    height: 576,
    objectPosition: 'center 40%',
    widths: OP_WIDTHS,
  }),
  opQualityControl: makeImage({
    id: 'op-quality-control',
    alt: 'A quality checker measuring a men’s shirt across the chest against a tape.',
    prefix: 'op-quality-control',
    width: 1024,
    height: 768,
    objectPosition: 'center 42%',
    widths: OP_WIDTHS,
  }),
  opPacking: makeImage({
    id: 'op-packing',
    alt: 'Packed men’s shirts in poly bags being placed into cartons for dispatch.',
    prefix: 'op-packing',
    width: 1024,
    height: 768,
    objectPosition: 'center 42%',
    widths: OP_WIDTHS,
  }),
  checkupPressure: makeImage({
    id: 'checkup-pressure',
    alt: 'A health check-up at NCPL’s Kolkata factories, with a blood-pressure reading being taken.',
    prefix: 'checkup-pressure',
    width: 1600,
    height: 735,
    objectPosition: 'center 45%',
    widths: CHECKUP_WIDTHS,
  }),
  checkupDoctor: makeImage({
    id: 'checkup-doctor',
    alt: 'Doctors at the health check-up held by Landmark Group Foundation at the factories.',
    prefix: 'checkup-doctor',
    width: 1600,
    height: 735,
    objectPosition: 'center 40%',
    widths: CHECKUP_WIDTHS,
  }),
  checkupConsult: makeImage({
    id: 'checkup-consult',
    alt: 'Workers speaking with the health-check team during the camp at the factories.',
    prefix: 'checkup-consult',
    width: 1600,
    height: 735,
    objectPosition: 'center 42%',
    widths: CHECKUP_WIDTHS,
  }),
  portraitPawan: makeImage({
    id: 'portrait-pawan',
    alt: 'Pawan Jhunjhunwala, Founder and Director of Narayanee Clothing.',
    prefix: 'portrait-pawan',
    width: 820,
    height: 820,
    objectPosition: 'center',
    widths: PORTRAIT_WIDTHS,
  }),
  portraitManeesha: makeImage({
    id: 'portrait-maneesha',
    alt: 'Maneesha Jhunjhunwala.',
    prefix: 'portrait-maneesha',
    width: 500,
    height: 500,
    objectPosition: 'center',
    widths: [480, 500],
  }),
  portraitPiyush: makeImage({
    id: 'portrait-piyush',
    alt: 'Piyush Jhunjhunwala, Director of Narayanee Clothing.',
    prefix: 'portrait-piyush',
    width: 820,
    height: 820,
    objectPosition: 'center',
    widths: PORTRAIT_WIDTHS,
  }),
  portraitRitesh: makeImage({
    id: 'portrait-ritesh',
    alt: 'Ritesh Kumar, Chief Operating Officer at Narayanee Clothing.',
    prefix: 'portrait-ritesh',
    width: 820,
    height: 820,
    objectPosition: 'center',
    widths: PORTRAIT_WIDTHS,
  }),
  portraitMegha: makeImage({
    id: 'portrait-megha',
    alt: 'Megha Jhunjhunwala, Technology and Digital Transformation at Narayanee Clothing.',
    prefix: 'portrait-megha',
    width: 800,
    height: 800,
    objectPosition: 'center',
    widths: [480, 800],
  }),
  portraitSachin: makeImage({
    id: 'portrait-sachin',
    alt: 'Sachin Kumar, Chief Merchandising Officer at Narayanee Clothing.',
    prefix: 'portrait-sachin',
    width: 640,
    height: 640,
    objectPosition: 'center',
    widths: [480, 640],
  }),
  placeDirectorsOffice: makeImage({
    id: 'place-directors-office',
    alt: 'A meeting room with a folded white shirt and fabric swatches on a wooden table, and a sewing floor visible through the window.',
    prefix: 'place-directors-office',
    width: 1024,
    height: 576,
    objectPosition: 'center 72%',
    widths: OP_WIDTHS,
  }),
  sedexMember: {
    id: 'sedex-member',
    alt: 'Sedex Member',
    width: 1400,
    height: 158,
    positive: '/images/sedex-member-pos.png',
    negative: '/images/sedex-member-neg.png',
    fallback: '/images/sedex-member-pos.png',
  },
};

export const ogImage = {
  path: '/images/og-image.jpg',
  alt: 'A white men’s formal shirt being checked at the collar on a navy table, with a sewing floor behind.',
  width: 1200,
  height: 630,
};
